// STARWEAVE — a buildable/disposable home world. Wraps the detailed cube-sphere
// Planet plus its atmosphere, clouds, ocean, and surface flora into one unit so
// the hyperdrive can dispose the current world and generate a brand-new one
// (different archetype, terrain, colour, life) on every jump.

import * as THREE from 'three';
import { Planet, makeAtmosphere, makeClouds, makeOcean } from './planet';
import { scatterSurface } from './flora';
import { ARCHETYPES, ARCHETYPE_COLORS, type WorldgenExports } from './worldgen';

export interface WorldTextures {
  rock: THREE.Texture;
  grass: THREE.Texture;
  sand: THREE.Texture;
  lava: THREE.Texture;
}

export interface World {
  planet: Planet;
  animated: THREE.Mesh[];          // clouds / ocean (time-driven shaders)
  objects: THREE.Object3D[];       // everything added to the scene (for disposal)
  archetype: number;
  label: string;                   // "LushLiving (g,s,b)"
  name: string;                    // short archetype name, for nav
  radiusKm: number;
}

export function buildWorld(
  scene: THREE.Scene, wg: WorldgenExports | null, universe: bigint,
  g: number, s: number, b: number, R: number,
  tex: WorldTextures, sunDir: THREE.Vector3, anchor: THREE.Vector3,
): World {
  let archetype = 1, radiusKm = 6000, water = 0, noiseSeed = 1, label = '(loading)';
  if (wg) {
    archetype = wg.sw_planet_archetype(universe, g, s, b);
    radiusKm = wg.sw_planet_radius_km(universe, g, s, b);
    water = wg.sw_planet_water_permille(universe, g, s, b);
    noiseSeed = Number(BigInt.asUintN(31, wg.sw_planet_visual_seed(universe, g, s, b)));
    label = `${ARCHETYPES[archetype] ?? '?'} (${g},${s},${b})`;
  }

  const low = new THREE.Color(ARCHETYPE_COLORS[archetype] ?? 0x888888).multiplyScalar(0.82);
  low.lerp(new THREE.Color(0x1a4fb0), Math.min(water / 1000, 1) * 0.5);
  const high = new THREE.Color(ARCHETYPE_COLORS[archetype] ?? 0x888888).lerp(new THREE.Color(0xffffff), 0.45);
  let lowTex: THREE.Texture | undefined;
  if (archetype === 0 || archetype === 4) lowTex = tex.grass;
  else if (archetype === 3) lowTex = tex.sand;
  else if (archetype === 6) lowTex = tex.lava;

  const planet = new Planet({
    radius: R, seed: noiseSeed, amplitude: R * 0.08, lowColor: low, highColor: high,
    detailTexture: tex.rock, lowTexture: lowTex,
  });
  scene.add(planet.group);
  const objects: THREE.Object3D[] = [planet.group];
  const animated: THREE.Mesh[] = [];

  const atmoIntensity = archetype === 2 ? 0.15 : archetype === 0 || archetype === 4 ? 0.75 : 0.5;
  const atmoColor = new THREE.Color(archetype === 4 || water > 500 ? 0x6fb6ff : 0x9ab8ff);
  const atmo = makeAtmosphere(R * 1.09, atmoColor, atmoIntensity, sunDir);
  scene.add(atmo); objects.push(atmo);

  if (atmoIntensity > 0.3) {
    const clouds = makeClouds(R * 1.02, sunDir);
    scene.add(clouds); animated.push(clouds); objects.push(clouds);
  }
  if (water > 300) {
    const seaLevel = R * (1 + 0.08 * (0.2 + (water / 1000) * 0.6));
    const oceanColor = new THREE.Color(water > 800 ? 0x12539a : 0x1a6fb0);
    const ocean = makeOcean(seaLevel, oceanColor, sunDir);
    scene.add(ocean); animated.push(ocean); objects.push(ocean);
  }

  const minH = water > 300 ? Math.min(0.78, 0.2 + (water / 1000) * 0.6 + 0.03) : 0;
  const flora = scatterSurface(scene, planet, anchor, archetype, noiseSeed, minH);
  objects.push(...flora);

  return { planet, animated, objects, archetype, label, name: ARCHETYPES[archetype] ?? 'World', radiusKm };
}

export function disposeWorld(scene: THREE.Scene, world: World): void {
  for (const o of world.objects) {
    o.traverse((c) => {
      const m = c as THREE.Mesh;
      if (m.geometry) m.geometry.dispose();
      const mat = m.material as THREE.Material | THREE.Material[] | undefined;
      if (Array.isArray(mat)) mat.forEach((x) => x.dispose());
      else if (mat) mat.dispose();
    });
    scene.remove(o);
  }
}
